/**
 * MitarbeiterSection — Vollständige Team-Verwaltung
 * 
 * Echte Daten aus Supabase, Admin-Funktionen, Online-Status,
 * Berechtigungen, Profil-Bearbeitung
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Users, Mail, Phone, Shield, Building2, Key, UserCheck, UserX,
  Edit2, Eye, Plus, RefreshCw, Search, ChevronDown, X,
} from 'lucide-react';
import { useAuth, type TeamMember } from '@/contexts/AuthContext';
import { useTeamAdmin } from '@/hooks/useTeamAdmin';
import MitarbeiterProfilModal from '@/components/MitarbeiterProfilModal';
import { BETRIEBE } from '@/lib/betriebe';
import { ROLLEN_LABELS, ROLLEN_COLORS } from '@/lib/mitarbeiter';
import { filterKiAgent } from '@/lib/kiAgent';
import { toast } from 'sonner';

const AVATAR_COLORS: Record<string, string> = {
  'AK': '#B91C1C', 'ID': '#2563EB', 'VM': '#059669',
  'TR': '#7C3AED', 'IH': '#D97706', 'AM': '#0D9488',
};

function SettingsCard({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-bold text-foreground">{title}</h3>
      </div>
      {description && <p className="text-xs text-muted-foreground mt-0.5 mb-4">{description}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

export default function MitarbeiterSection() {
  const { isAdmin, teamMembers, onlineUserIds, refreshTeam, profile } = useAuth();
  const { sendPasswordReset } = useTeamAdmin();
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRolle, setFilterRolle] = useState<string>('alle');
  const [refreshing, setRefreshing] = useState(false);

  // Team-Daten mit Online-Status anreichern
  const enrichedMembers = filterKiAgent(teamMembers, isAdmin).map(m => ({
    ...m,
    is_online: onlineUserIds.has(m.email),
  }));

  const onlineCount = enrichedMembers.filter(m => m.is_online).length;
  const activeCount = enrichedMembers.filter(m => m.ist_aktiv).length;

  // Filtern
  const filteredMembers = enrichedMembers.filter(m => {
    const matchesSearch = searchQuery === '' || 
      `${m.vorname} ${m.nachname}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRolle = filterRolle === 'alle' || m.rolle === filterRolle;
    return matchesSearch && matchesRolle;
  });

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refreshTeam();
    setRefreshing(false);
    toast.success('Team-Daten aktualisiert');
  }, [refreshTeam]);

  const openProfile = (member: TeamMember) => {
    setSelectedMember(member);
    setModalOpen(true);
  };

  const handleSaved = () => {
    refreshTeam();
    setModalOpen(false);
  };

  return (
    <>
      {/* Team-Übersicht */}
      <SettingsCard title="Team-Übersicht" description={`${onlineCount} von ${activeCount} Mitarbeitern online`}>
        <div className="flex items-center gap-3 flex-wrap">
          {enrichedMembers.filter(m => m.ist_aktiv).map((ma) => {
            const initialen = `${ma.vorname[0] || ''}${ma.nachname[0] || ''}`.toUpperCase();
            const avatarColor = AVATAR_COLORS[initialen] || '#6B7280';
            return (
              <button
                key={ma.id}
                onClick={() => openProfile(ma)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                  selectedMember?.id === ma.id
                    ? 'border-[#9B1C1C] bg-red-50/50 shadow-sm'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="relative">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: avatarColor }}
                  >
                    {initialen}
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                    ma.is_online ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                </div>
                <div className="text-left">
                  <p className="text-xs font-medium text-gray-800">{ma.vorname} {ma.nachname}</p>
                  <p className="text-[10px] text-gray-400">
                    {ma.is_online ? 'Online' : 'Offline'}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </SettingsCard>

      {/* Mitarbeiter-Tabelle */}
      <SettingsCard title="Alle Mitarbeiter" description="Konten verwalten, Rollen zuweisen, Berechtigungen konfigurieren">
        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Mitarbeiter suchen..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-gray-200 focus:border-[#9B1C1C] focus:ring-1 focus:ring-[#9B1C1C]/20 outline-none"
            />
          </div>
          <select
            value={filterRolle}
            onChange={e => setFilterRolle(e.target.value)}
            className="px-3 py-2 text-xs rounded-lg border border-gray-200 bg-white"
          >
            <option value="alle">Alle Rollen</option>
            <option value="admin">Administrator</option>
            <option value="sachbearbeiter">Sachbearbeiter</option>
            <option value="mitarbeiter">Mitarbeiter</option>
            <option value="kiosk">Kiosk</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2.5 pr-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Mitarbeiter</th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Kontakt</th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Rolle</th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Betriebe</th>
                <th className="text-left py-2.5 px-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                <th className="text-right py-2.5 pl-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((ma, idx) => {
                const initialen = `${ma.vorname[0] || ''}${ma.nachname[0] || ''}`.toUpperCase();
                const avatarColor = AVATAR_COLORS[initialen] || '#6B7280';
                const rolleKey = ma.rolle as keyof typeof ROLLEN_LABELS;
                return (
                  <tr
                    key={ma.id}
                    className={`border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer ${idx % 2 === 1 ? 'bg-gray-50/50' : ''}`}
                    onClick={() => openProfile(ma)}
                  >
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2.5">
                        <div className="relative">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                            style={{ backgroundColor: avatarColor }}
                          >
                            {initialen}
                          </div>
                          <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                            ma.is_online ? 'bg-green-500' : 'bg-gray-300'
                          }`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{ma.vorname} {ma.nachname}</p>
                          <p className="text-[10px] text-gray-400">
                            {ma.is_online ? 'Gerade aktiv' : 'Offline'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="space-y-0.5">
                        {ma.email && (
                          <div className="flex items-center gap-1 text-[11px] text-gray-500">
                            <Mail className="w-3 h-3" /> {ma.email}
                          </div>
                        )}
                        {ma.telefon && (
                          <div className="flex items-center gap-1 text-[11px] text-gray-500">
                            <Phone className="w-3 h-3" /> {ma.telefon}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${ROLLEN_COLORS[rolleKey] || 'bg-gray-100 text-gray-600'}`}>
                        {ROLLEN_LABELS[rolleKey] || ma.rolle}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        {(ma.business_units || []).slice(0, 3).map((buId) => {
                          const b = BETRIEBE[buId as keyof typeof BETRIEBE];
                          return (
                            <span key={buId} className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                              {(b as any)?.kuerzel || (b as any)?.name || buId}
                            </span>
                          );
                        })}
                        {(ma.business_units || []).length > 3 && (
                          <span className="text-[9px] text-gray-400">+{ma.business_units.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1.5">
                        {ma.is_online ? (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Online</span>
                        ) : (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Offline</span>
                        )}
                        {!ma.ist_aktiv && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600">Deaktiviert</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pl-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={(e) => { e.stopPropagation(); openProfile(ma); }}
                          className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                          title="Profil anzeigen"
                        >
                          <Eye className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openProfile(ma); }}
                            className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                            title="Profil bearbeiten"
                          >
                            <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              sendPasswordReset(ma.email);
                            }}
                            className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                            title="Passwort-Reset senden"
                          >
                            <Key className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredMembers.length === 0 && (
          <div className="text-center py-8 text-xs text-gray-400">
            Keine Mitarbeiter gefunden
          </div>
        )}

        {isAdmin && (
          <button
            onClick={() => toast('Mitarbeiter hinzufügen', { description: 'Neue Mitarbeiter werden über Supabase Auth angelegt. Kontaktiere den System-Administrator.' })}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-sm text-gray-400 hover:border-gray-400 hover:text-gray-500 transition-colors w-full justify-center mt-3"
          >
            <Plus className="w-4 h-4" />
            Mitarbeiter hinzufügen
          </button>
        )}
      </SettingsCard>

      {/* Rollen & Berechtigungen */}
      <SettingsCard title="Rollen & Berechtigungen" description="Zugriffsrechte pro Rolle">
        <div className="space-y-2">
          {(['admin', 'sachbearbeiter', 'mitarbeiter', 'kiosk'] as const).map((rolle) => {
            const count = enrichedMembers.filter(m => m.rolle === rolle).length;
            return (
              <div key={rolle} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full ${ROLLEN_COLORS[rolle]}`}>
                    {ROLLEN_LABELS[rolle]}
                  </span>
                  <span className="text-xs text-gray-500">
                    {rolle === 'admin' && 'Voller Zugriff auf alle Betriebe und Einstellungen'}
                    {rolle === 'sachbearbeiter' && 'Akten, Aufgaben, Kalender, Dokumente — kein Finanzen/Team'}
                    {rolle === 'mitarbeiter' && 'Eingeschränkt: Akten lesen/erstellen, Kalender lesen, Aufgaben'}
                    {rolle === 'kiosk' && 'Nur Kiosk-Ansicht für Kundenempfang'}
                  </span>
                </div>
                <span className="text-[10px] text-gray-400">{count} Mitarbeiter</span>
              </div>
            );
          })}
        </div>
      </SettingsCard>

      {/* Profil-Modal */}
      <MitarbeiterProfilModal
        member={selectedMember}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
      />
    </>
  );
}
